import React, { useState } from 'react';
import { Box, Typography, Container, Grid, Table, TableBody, TableCell, TableContainer, TableRow, Paper, Accordion, AccordionSummary, AccordionDetails, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import SchoolIcon from '@mui/icons-material/School';
import FactoryIcon from '@mui/icons-material/Factory';
import BusinessIcon from '@mui/icons-material/Business';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import WcIcon from '@mui/icons-material/Wc';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import NatureIcon from '@mui/icons-material/Nature';

import { SiGooglepay, SiPhonepe, SiPaytm } from 'react-icons/si';
import { MdQrCodeScanner, MdPayment } from 'react-icons/md';
import LockIcon from '@mui/icons-material/Lock';
import LocationCityIcon from '@mui/icons-material/LocationCity';

import imgVending from '../images/vending/OUR PRODUCTS/Sanitary Napkin Vending Machines.jpg';
import imgIncinerator from '../images/vending/OUR PRODUCTS/Sanitary Napkin Incinerator.png';
import imgHomeIncinerator from '../images/vending/OUR PRODUCTS/Home Sanitary Napkin Incinerator.png';

const productsData = [
  {
    title: 'Sanitary Napkin Vending Machines',
    image: imgVending,
    types: [
      'Manual Vending Machine',
      'Automatic Vending Machine'
    ],
    features: [
      { label: 'Product', value: 'Folded sanitary napkins' },
      { label: 'Mounting', value: 'Wall-mounted / Stand' },
      { label: 'Storage Capacity', value: '25, 30, 40 pads (manual)' },
      { label: 'Size of Napkin', value: 'Customizable' },
      { label: 'Loading', value: 'Vertical / Horizontal' },
      { label: 'Surface Finish', value: 'Powder coating' },
    ],
    techSpecs: {
      title: 'Automatic Vending Machine – Technical Specs',
      specs: [
        'Storage Capacity: 40, 60, 90 pads',
        'Display: Digital (automatic)',
        'Operation: Automatic',
        'Coin / Token / Button / Sensor operation / Upi',
        'Voltage: 230V',
        'Frequency: 50 Hz',
        'Phase: Single',
        'Weight: 15–30 kg',
        'Dimensions: Based on capacity',
        'Body Material: Powder-coated metal'
      ]
    },
    bottomSections: [
      {
        type: 'intro',
        title: 'Fem Tech Solution – Empowering Women with Comfort & Dignity',
        text: [
          'At Fem Tech Solution, we believe menstrual hygiene should never be a barrier. Our Sanitary Napkin Vending Machines ensure women and girls have reliable, respectful, and instant access to sanitary napkins—whenever they need them.',
          'Installed inside girls’ and ladies’ restrooms, our machines are designed for ease, affordability, and dignity, supporting women in every environment.'
        ]
      },
      {
        type: 'list_with_icons',
        title: 'Why Choose Our Vending Machines?',
        items: [
          { icon: 'time', title: '24/7 Availability', desc: 'Access sanitary napkins anytime—perfect for emergencies, day or night.' },
          { icon: 'money', title: 'Affordable & Accessible', desc: 'Napkins available at low-cost or subsidized rates for maximum reach.' },
          { icon: 'flash', title: 'Instant Relief', desc: 'No waiting, no stress—support exactly when it’s needed most.' }
        ]
      },
      {
        type: 'grid_icons',
        title: 'Ideal Installation Locations',
        items: [
          { icon: 'school', text: 'Schools & Colleges' },
          { icon: 'factory', text: 'Factories & Industrial Units' },
          { icon: 'business', text: 'Offices & Corporate Spaces' },
          { icon: 'hospital', text: 'Hospitals & Clinics' },
          { icon: 'wc', text: 'Public Toilets' },
          { icon: 'city', text: 'Rural & Urban Institutions' }
        ]
      },
      {
        type: 'digital',
        title: 'Digital & Contactless Access',
        text: 'Welcome to smart hygiene solutions. Our machines support cashless payments, ensuring safe and seamless access.',
        subText: 'Supported Payment Options:',
        paymentIcons: [
          { icon: <SiGooglepay size={28} color="#5F6368" />, label: 'Google Pay' },
          { icon: <SiPhonepe size={28} color="#6739b7" />, label: 'PhonePe' },
          { icon: <SiPaytm size={28} color="#00BAF2" />, label: 'Paytm' },
          { icon: <MdPayment size={32} color="#333" />, label: 'UPI / BHIM' },
          { icon: <MdQrCodeScanner size={32} color="#333" />, label: 'Scan & Pay' }
        ],
        footer: 'Say goodbye to inconvenience. Say hello to dignity.'
      }
    ]
  },
  {
    title: 'Sanitary Napkin Incinerator',
    image: imgIncinerator,
    models: [
      'Small', 'Medium', 'Big', 'High Capacity'
    ],
    specifications: [
      { label: 'Temperature Control', value: 'Analog / Digital' },
      { label: 'Mounting', value: 'Wall / Stand' },
      { label: 'Loading', value: 'Front' },
      { label: 'Capacity', value: '30 to 250 napkins per day' },
      { label: 'Startup', value: 'Power-saving mode' },
      { label: 'Starter', value: 'MCB' },
      { label: 'Electrical', value: '230V ±10%, 50Hz, Single Phase, 5A, 2KW' },
      { label: 'Weight', value: '7–25 kg' }
    ],
    topSections: [
      {
        type: 'intro',
        title: 'Fem Tech Solution – Safe Disposal. Cleaner Spaces. Greener Future.',
        text: [
          'Proper disposal is just as important as access. Fem Tech Solution Sanitary Napkin Incinerators offer a hygienic, discreet, and eco-conscious way to dispose of used sanitary napkins.',
          'Designed for installation inside women’s restrooms, our incinerators help maintain cleanliness while protecting the environment.'
        ]
      },
      {
        type: 'list_with_icons',
        title: 'Key Benefits',
        items: [
          { icon: 'eco', title: 'Eco-Friendly Disposal', desc: 'Minimizes land and water pollution through controlled, safe incineration.' },
          { icon: 'lock', title: 'Privacy & Hygiene', desc: 'Ensures discreet disposal, promoting confidence and cleanliness.' },
          { icon: 'time', title: '24/7 Cleanliness', desc: 'Anytime disposal keeps restrooms fresh, safe, and odor-free.' }
        ]
      },
      {
        type: 'text_highlight',
        text: 'Fem Tech Solution is committed to creating healthier spaces and empowering women through innovative hygiene technology.'
      }
    ]
  },
  {
    title: 'Home Sanitary Napkin Incinerator',
    image: imgHomeIncinerator,
    specifications: [
      { label: 'Application', value: 'Household sanitary napkin disposal' },
      { label: 'Installation', value: 'Wall-mounted / Floor-standing' },
      { label: 'Operation', value: 'Manual / Automatic*' },
      { label: 'Capacity', value: 'Based on model' },
      { label: 'Loading', value: 'Front / Top loading' },
      { label: 'Heating System', value: 'Electric heating system' },
      { label: 'Control', value: 'Switch / Digital control*' },
      { label: 'Body Material', value: 'Powder-coated metal / Stainless steel*' },
      { label: 'Power Supply', value: '230V AC' },
      { label: 'Frequency', value: '50 Hz' },
      { label: 'Phase', value: 'Single Phase' },
      { label: 'Temperature Control', value: 'Controlled heating' },
      { label: 'Design', value: 'Compact & space-efficient' },
      { label: 'Maintenance', value: 'Easy cleaning & maintenance' }
    ],
    techSpecs: {
      title: 'Home Incinerator – Technical Specifications',
      specs: [
        'Power Supply: 230V AC',
        'Frequency: 50 Hz',
        'Phase: Single Phase',
        'Heating: Electric heating element',
        'Operation: Manual / Automatic based on model',
        'Capacity: Model-specific',
        'Temperature Control: Controlled thermal operation',
        'Body: Durable metal construction',
        'Finish: Powder-coated / Stainless steel based on model',
        'Installation: Indoor / Suitable ventilated area',
        'Application: Used sanitary napkin disposal'
      ]
    },
    topSections: [
      {
        type: 'intro',
        title: 'Fem Tech Solution – Safe, Hygienic & Responsible Disposal at Home',
        text: [
          'At Fem Tech Solution, we believe menstrual hygiene includes not only access to sanitary products but also their safe and hygienic disposal. Our Home Sanitary Napkin Incinerators provide a convenient and responsible way to dispose of used sanitary napkins within the comfort and privacy of your home.',
          'Designed for easy operation, compact installation, and hygienic disposal, our incinerators help reduce unpleasant odours, household waste, and improper disposal of sanitary products.'
        ]
      },
      {
        type: 'list_with_icons',
        title: 'Why Choose Our Home Incinerator?',
        items: [
          { icon: 'lock', title: 'Safe & Hygienic Disposal', desc: 'Dispose of used sanitary napkins safely without storing menstrual waste in household bins for long periods.' },
          { icon: 'time', title: 'Convenient for Home Use', desc: 'Compact and easy-to-use design suitable for bathrooms, utility areas, and other convenient locations.' },
          { icon: 'eco', title: 'Odour Reduction', desc: 'Helps minimize unpleasant odours associated with used sanitary napkin waste.' },
          { icon: 'lock', title: 'Private & Dignified', desc: 'Provides women with a discreet disposal solution within their own home.' },
          { icon: 'flash', title: 'Easy Operation', desc: 'Simple controls make the unit convenient for everyday household use.' }
        ]
      },
      {
        type: 'grid_icons',
        title: 'Ideal Installation Locations',
        items: [
          { icon: 'city', text: 'Homes & Apartments' },
          { icon: 'city', text: 'Independent Houses' },
          { icon: 'business', text: 'Hostels & Paying Guest Accommodation' },
          { icon: 'business', text: 'Women’s Residences' },
          { icon: 'business', text: 'Staff Quarters' },
          { icon: 'city', text: 'Rural & Urban Homes' }
        ]
      },
      {
        type: 'text_highlight',
        text: 'Our home incinerator is designed to support responsible menstrual waste management by safely treating used sanitary napkins through controlled thermal disposal.'
      }
    ],
    bottomSections: [
      {
        type: 'list_with_icons',
        title: 'Benefits for Your Home',
        items: [
          { icon: 'eco', title: 'Hygienic menstrual waste disposal', desc: 'Minimizes health risks' },
          { icon: 'time', title: 'Compact and convenient design', desc: 'Fits easily in modern spaces' },
          { icon: 'eco', title: 'Reduces household waste', desc: 'Prevents menstrual waste accumulation' },
          { icon: 'time', title: 'Maintains bathroom cleanliness', desc: 'Odor-free and clean environments' },
          { icon: 'lock', title: 'Private and dignified disposal', desc: 'Comfortable usage at home' },
          { icon: 'flash', title: 'Easy everyday operation', desc: 'Designed for simple daily use' },
          { icon: 'city', title: 'Suitable for modern homes', desc: 'Sleek and highly functional' }
        ]
      },
      {
        type: 'text_highlight',
        text: 'A Cleaner Home. A Safer Disposal Solution. A More Dignified Period. Fem Tech Solution – Empowering Women with Comfort, Hygiene & Dignity.'
      }
    ]
  }
];

const iconMapping = {
  time: AccessTimeIcon,
  money: MonetizationOnIcon,
  flash: FlashOnIcon,
  school: SchoolIcon,
  factory: FactoryIcon,
  business: BusinessIcon,
  hospital: LocalHospitalIcon,
  wc: WcIcon,
  city: LocationCityIcon,
  eco: NatureIcon,
  lock: LockIcon,
};

function Services() {
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const renderExtraSections = (sections) => {
    if (!sections) return null;
    return sections.map((section, idx) => {
      switch (section.type) {
        case 'intro':
          return (
            <Box key={idx} sx={{ mb: 5, textAlign: 'left' }}>
              <Typography variant="h6" sx={{ color: '#7e57c2', fontWeight: 'bold', mb: 2 }}>
                {section.title}
              </Typography>
              {section.text.map((t, i) => (
                <Typography key={i} sx={{ color: '#555', mb: 1.5, lineHeight: 1.7, fontSize: '15px' }}>{t}</Typography>
              ))}
            </Box>
          );
        case 'list_with_icons':
          return (
            <Box key={idx} sx={{ mb: 5 }}>
              <Typography variant="h6" sx={{ color: '#003366', fontWeight: 'bold', mb: 3 }}>
                {section.title}
              </Typography>
              <Grid container spacing={2}>
                {section.items.map((item, i) => {
                  const Icon = iconMapping[item.icon];
                  return (
                    <Grid item xs={12} sm={6} key={i}>
                      <Paper elevation={0} sx={{ p: 3, border: '1px solid #f0f0f0', height: '100%', backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          {Icon && <Icon sx={{ color: '#7e57c2', mr: 1.5, fontSize: '28px' }} />}
                          <Typography variant="subtitle1" sx={{ fontWeight: '700', color: '#333' }}>{item.title}</Typography>
                        </Box>
                        {item.desc && <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6 }}>{item.desc}</Typography>}
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          );
        case 'grid_icons':
          return (
            <Box key={idx} sx={{ mb: 5 }}>
              <Typography variant="h6" sx={{ color: '#003366', fontWeight: 'bold', mb: 3 }}>
                {section.title}
              </Typography>
              <Grid container spacing={2}>
                {section.items.map((item, i) => {
                  const Icon = iconMapping[item.icon];
                  return (
                    <Grid item xs={12} sm={6} key={i}>
                      <Box sx={{ display: 'flex', alignItems: 'center', p: 2, backgroundColor: '#f9f9fc', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
                        {Icon && <Icon sx={{ color: '#7e57c2', mr: 2, fontSize: '24px' }} />}
                        <Typography variant="body2" sx={{ fontWeight: '600', color: '#444' }}>{item.text}</Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          );

        case 'digital':
          return (
            <Box key={idx} sx={{ mb: 5, backgroundColor: '#fdfbfe', p: 4, borderRadius: '16px', border: '1px solid #ede7f6' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <SmartphoneIcon sx={{ color: '#7e57c2', mr: 2, fontSize: 32 }} />
                <Typography variant="h6" sx={{ color: '#003366', fontWeight: 'bold' }}>
                  {section.title}
                </Typography>
              </Box>
              <Typography sx={{ color: '#555', mb: 3, lineHeight: 1.7 }}>{section.text}</Typography>

              {section.subText && (
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#333', mb: 2 }}>
                  {section.subText}
                </Typography>
              )}

              {section.paymentIcons && (
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
                  {section.paymentIcons.map((pay, i) => (
                    <Box key={i} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ backgroundColor: '#fff', p: 2, borderRadius: '12px', boxShadow: '0 4px 10px rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {pay.icon}
                      </Box>
                      <Typography variant="caption" sx={{ color: '#555', fontWeight: '600' }}>{pay.label}</Typography>
                    </Box>
                  ))}
                </Box>
              )}

              <Typography sx={{ mt: 3, color: '#7e57c2', fontWeight: 'bold', fontStyle: 'italic', borderTop: '1px dashed #e0e0e0', pt: 3 }}>
                {section.footer}
              </Typography>
            </Box>
          );
        case 'text_highlight':
          return (
            <Box key={idx} sx={{ mt: 4, p: 3, backgroundColor: '#f0e6ff', borderLeft: '4px solid #7e57c2', borderRadius: '0 8px 8px 0' }}>
              <Typography variant="body1" sx={{ color: '#4d2c91', fontWeight: '600', fontStyle: 'italic', lineHeight: 1.6 }}>
                {section.text}
              </Typography>
            </Box>
          );
        default:
          return null;
      }
    });
  };

  return (
    <Box sx={{ padding: '80px 0', backgroundColor: '#fdfdfd', borderTop: '1px solid #ddd' }}>
      <Container maxWidth="lg">
        {/* Page Header */}
        <Box textAlign="center" mb={10}>
            <Typography variant="h6" sx={{ color: '#7e57c2', fontWeight: 'bold', mb: 1, letterSpacing: '2px', textTransform: 'uppercase', fontSize: '14px' }}>
                What We Offer
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#003366', textTransform: 'uppercase' }}>
                OUR PRODUCTS
            </Typography>
            <Typography sx={{ fontSize: '16px', color: '#666', mt: 3, maxWidth: '700px', mx: 'auto', lineHeight: 1.8 }}>
                Fem Tech Solutions delivers practical, durable, and hygienic solutions for menstrual health and personal protection. Our products are designed for educational institutions, hospitals, factories, and workplaces.
            </Typography>
        </Box>

        {/* List of Products */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {productsData.map((product, index) => (
             <Accordion
                key={index}
                expanded={expanded === index}
                onChange={handleChange(index)}
                sx={{
                  backgroundColor: '#fff',
                  border: '1px solid #eaeaea',
                  borderRadius: '16px !important',
                  boxShadow: expanded === index ? '0 10px 40px rgba(126, 87, 194, 0.08)' : '0 4px 15px rgba(0,0,0,0.02)',
                  transition: 'all 0.3s ease',
                  overflow: 'hidden',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: '#7e57c2', fontSize: '32px' }} />}
                  sx={{
                    px: { xs: 3, md: 5 },
                    py: 2,
                    backgroundColor: expanded === index ? '#fcfaff' : '#fff',
                    borderBottom: expanded === index ? '1px solid #f0f0f0' : 'none',
                    '&:hover': { backgroundColor: '#fcfaff' }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                     <Box sx={{ width: '8px', height: '32px', backgroundColor: '#7e57c2', borderRadius: '4px', mr: 3 }} />
                     <Typography variant="h5" sx={{ fontWeight: '700', color: '#003366', letterSpacing: '0.5px' }}>
                       {product.title}
                     </Typography>
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ p: { xs: 3, md: 6 }, backgroundColor: '#fff' }}>
                    <Grid container spacing={8}>
                        {/* LEFT COLUMN: Descriptions & Features */}
                        <Grid item xs={12} md={7}>
                            {/* Product Image Showcase */}
                            {product.image && (
                                <Box sx={{ 
                                    mb: 5, 
                                    borderRadius: '16px', 
                                    overflow: 'hidden', 
                                    boxShadow: '0 8px 30px rgba(126, 87, 194, 0.08)', 
                                    border: '1px solid #f0f0f0', 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    backgroundColor: '#fff', 
                                    p: 4 
                                }}>
                                    <img 
                                        src={product.image} 
                                        alt={product.title} 
                                        style={{ 
                                            width: '100%', 
                                            maxHeight: '400px', 
                                            objectFit: 'contain', 
                                            display: 'block' 
                                        }} 
                                    />
                                </Box>
                            )}

                            {product.topSections && renderExtraSections(product.topSections)}
                            
                            {/* Types */}
                            {product.types && (
                                <Box sx={{ mb: 5 }}>
                                    <Typography variant="h6" sx={{ color: '#003366', fontWeight: 'bold', mb: 2 }}>
                                        AVAILABLE TYPES
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        {product.types.map((type, i) => (
                                            <Box key={i} sx={{ px: 2.5, py: 1, backgroundColor: '#f0e6ff', borderRadius: '50px', border: '1px solid #d1b3ff' }}>
                                                <Typography sx={{ color: '#4d2c91', fontWeight: '600', fontSize: '14px' }}>{type}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {/* Models */}
                            {product.models && (
                                <Box sx={{ mb: 5 }}>
                                    <Typography variant="h6" sx={{ color: '#003366', fontWeight: 'bold', mb: 2 }}>
                                        AVAILABLE MODELS
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        {product.models.map((mod, i) => (
                                            <Box key={i} sx={{ px: 2.5, py: 1, backgroundColor: '#f0e6ff', borderRadius: '50px', border: '1px solid #d1b3ff' }}>
                                                <Typography sx={{ color: '#4d2c91', fontWeight: '600', fontSize: '14px' }}>{mod}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {product.bottomSections && renderExtraSections(product.bottomSections)}
                        </Grid>

                        {/* RIGHT COLUMN: Specs & Tables */}
                        <Grid item xs={12} md={5}>
                            <Box sx={{ backgroundColor: '#f9f9fc', p: 4, borderRadius: '16px', border: '1px solid #f0f0f0', position: 'sticky', top: '100px' }}>
                                {(product.features || product.specifications) && (
                                    <Box sx={{ mb: 5 }}>
                                        <Typography variant="h6" sx={{ color: '#003366', fontWeight: 'bold', mb: 3 }}>
                                            KEY SPECIFICATIONS
                                        </Typography>
                                        <TableContainer sx={{ backgroundColor: 'transparent' }}>
                                            <Table size="small">
                                                <TableBody>
                                                    {(product.features || product.specifications).map((item, i) => (
                                                        <TableRow key={i} sx={{ '& td': { borderBottom: '1px solid #eee', py: 1.5, px: 0 } }}>
                                                            <TableCell sx={{ fontWeight: '600', color: '#555', width: '45%' }}>{item.label}</TableCell>
                                                            <TableCell sx={{ color: '#333' }}>{item.value}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                )}

                                {product.techSpecs && (
                                    <Box>
                                        <Typography variant="h6" sx={{ color: '#003366', fontWeight: 'bold', mb: 3 }}>
                                            {product.techSpecs.title || 'TECHNICAL DETAILS'}
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                            {product.techSpecs.specs.map((spec, i) => (
                                                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                    <Box sx={{ width: '6px', height: '6px', backgroundColor: '#7e57c2', borderRadius: '50%', mt: 1, mr: 1.5, flexShrink: 0 }} />
                                                    <Typography sx={{ fontSize: '14.5px', color: '#444', lineHeight: 1.6 }}>{spec}</Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                )}

                                <Box sx={{ mt: 5 }}>
                                    <Button 
                                        variant="contained" 
                                        component={Link} 
                                        to="/contact" 
                                        fullWidth
                                        size="large"
                                        sx={{ 
                                            py: 1.5, 
                                            fontWeight: 'bold', 
                                            fontSize: '15px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '1px',
                                            borderRadius: '50px',
                                            backgroundColor: '#7e57c2',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                backgroundColor: '#5e35b1',
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 8px 20px rgba(126, 87, 194, 0.3)'
                                            }
                                        }}
                                    >
                                        Enquire Now
                                    </Button>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </AccordionDetails>
              </Accordion>
          ))}
        </Box>
      </Container>
    </Box>
  );
}

export default Services;
